import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useUserInfoStore } from '@space-shell/core/store'

import { CheckoutWrapperId, type TransactionData } from './use-checkout.types'

import { ScriptStatus, useScript } from '../use-script'

export type UseCheckoutParams = {
  offerKey: string
  coupon?: string
  sck: string
  onCheckoutError: (error) => void
  onPostCheckout: (transactionData: TransactionData) => void
  campaignCode?: string
}

const useCheckout = ({
  sck,
  offerKey,
  coupon,
  campaignCode,
  onCheckoutError,
  onPostCheckout
}: UseCheckoutParams) => {
  const user = useUserInfoStore()
  const checkoutRef = useRef<HTMLDivElement>(null)
  const [isLoadingCheckout, setIsLoadingCheckout] = useState<boolean>(true)
  const [isMounted, setIsMouted] = useState<boolean>(false)

  const checkoutSrc = process.env.CHECKOUT_TRANSPARENT_SCRIPT as string

  const scriptCustomParams: { [key: string]: unknown } = {
    defer: false,
    type: 'text/javascript',
    id: 'checkout-elements-script'
  }

  const hotpayScriptStatus = useScript(checkoutSrc, scriptCustomParams)

  const checkoutElement = useMemo(() => {
    if (hotpayScriptStatus !== ScriptStatus.Ready) return

    return window.checkoutElements.init('inlineCheckout', {
      offer: offerKey,
      prefilledInfo: {
        sck,
        name: user.name,
        email: user.email,
        offDiscount: coupon || ''
      },
      payment: {
        onError: onCheckoutError,
        onSuccess: onPostCheckout
      },
      onLoad: () => {
        setIsLoadingCheckout(false)
        setIsMouted(true)
      },
      visibilityOptions: {
        hidePix: 'false',
        xcod: campaignCode,
        hideBillet: 'false',
        hidePaypal: 'false',
        showproductinfo: 'true'
      }
    })
  }, [
    offerKey,
    coupon,
    user.name,
    user.email,
    onCheckoutError,
    campaignCode,
    sck,
    hotpayScriptStatus,
    onPostCheckout
  ])

  const loadCheckoutIframe = useCallback(() => {
    checkoutElement?.mount(`#${CheckoutWrapperId}`)
  }, [checkoutElement])

  useEffect(() => {
    if (!isMounted) loadCheckoutIframe()
  }, [loadCheckoutIframe, checkoutElement, isMounted])

  return {
    checkoutRef,
    isLoadingCheckout,
    loadCheckoutIframe
  }
}

export default useCheckout
